import React, { useState } from 'react';
import { Box, TextField, Button, Typography, Container, Card, CardContent, Divider, Select, MenuItem, Slider, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { jsPDF } from "jspdf";
import Footer from './Footer';
import axios from 'axios'; // Import axios for making API calls
import Pricing from './Pricing';

const CostCalculator = () => {
    const [formData, setFormData] = useState({
        area: 0,
        package: 'Basic',
        parking: 0,
        sump: 0,
        recyclingTank: 0,
        compoundWall: 0,
        solarPower: 0
    });

    const [openModal, setOpenModal] = useState(false);
    const [userDetails, setUserDetails] = useState({
        name: '',
        contact: '',
        email: '',
        address: '',
        additionalInfo: ''
    });

    const packagePrices = {
        Basic: 1899,
        Standard: 2099,
        Premium: 2449
    };

    const calculateCost = () => {
        const parkingCost = formData.parking * 1800;
        const sumpCost = formData.sump * 25;
        const recyclingCost = formData.recyclingTank * 30000;
        const wallCost = formData.compoundWall * 2000;
        const solarCost = formData.solarPower * 75000;
        const areaCost = formData.area * packagePrices[formData.package];

        return areaCost + parkingCost + sumpCost + recyclingCost + wallCost + solarCost;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value
        }));
    };

    const handleSliderChange = (name, value) => {
        setFormData((prevData) => ({
            ...prevData,
            [name]: value
        }));
    };

    const handleOpenModal = () => {
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
    };

    // Handle the submission of the user form inside the modal
    const handleFormSubmit = () => {
        generatePDF().then(pdfBase64 => {
            // Download the PDF
            downloadPDF(pdfBase64);
            // Send the email using Brevo directly from the frontend
            sendEmailWithAttachment(pdfBase64);
        });
        setOpenModal(false);
    };

    // Function to generate PDF and return it as a base64 URL
        const generatePDF = () => {
            return new Promise((resolve) => {
                const doc = new jsPDF();

                doc.setFontSize(16);
                doc.setFont("helvetica", "bold");
                doc.text("Construction Cost Invoice", 20, 20);

                doc.setLineWidth(0.5);
                doc.line(20, 25, 190, 25);

                doc.setFontSize(12);
                doc.setFont("helvetica", "normal");

                // Add company details
                doc.text("CaRE Constructions", 20, 40);
                doc.text("Address: Chennai-603103, Tamil Nadu, India.", 20, 45);
                doc.text("Phone: +91 90875 98811", 20, 50);
                doc.text("Email: dhamu@carelab.in", 20, 55);

                // Add client details
                doc.text("Client Name: " + userDetails.name, 120, 40);
                doc.text("Email: " + userDetails.email, 120, 45);
                doc.text("Phone: " + userDetails.contact, 120, 50);
                doc.text("Address: " + userDetails.address, 120, 55);

                doc.line(20, 60, 190, 60);

                // Table content
                const items = [
                    { label: "Total construction area (sq.ft)", value: formData.area * packagePrices[formData.package] },
                    { label: "Car parking area (sq.ft)", value: formData.parking * 1800 },
                    { label: "Underground sump (litres)", value: formData.sump * 25 },
                    { label: "Waste water recycling tanks", value: formData.recyclingTank * 30000 },
                    { label: "Compound wall length (ft)", value: formData.compoundWall * 2000 },
                    { label: "Solar power requirement", value: formData.solarPower * 75000 }
                ];

                let y = 80;
                items.forEach(item => {
                    doc.text(item.label, 20, y);
                    doc.text(`₹${item.value.toLocaleString()}`, 140, y);
                    y += 10;
                });

                doc.line(20, y, 190, y);
                y += 10;

                doc.setFont("helvetica", "bold");
                doc.text("Total Estimate", 20, y);
                doc.text(`₹${calculateCost().toLocaleString()}`, 140, y);

                y += 20;
                doc.setFontSize(10);
                doc.setFont("helvetica", "italic");
                doc.text("Thank you for choosing our services!", 20, y);

                // Convert PDF to Base64
                const pdfBase64 = doc.output('datauristring').split(',')[1];
                resolve(pdfBase64);
            });
        };

        // Function to trigger the download of the PDF
        const downloadPDF = (base64Pdf) => {
            const linkSource = `data:application/pdf;base64,${base64Pdf}`;
            const downloadLink = document.createElement("a");
            const fileName = "construction_invoice.pdf";

            downloadLink.href = linkSource;
            downloadLink.download = fileName;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
        };

        // Function to send the email with attachment using Brevo API directly from the frontend
        const sendEmailWithAttachment = async (pdfBase64) => {
            // **SECURITY WARNING:**
            // It is highly recommended to handle your Brevo API key on a backend server
            // instead of exposing it in your frontend code. This is for security reasons.
            const brevoApiKey = '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'; // Replace with your actual Brevo API key

            if (brevoApiKey === 'YOUR_BREVO_API_KEY') {
                alert("Please replace 'YOUR_BREVO_API_KEY' with your actual Brevo API key.");
                return;
            }

            const emailContent = `
                <p>Dear ${userDetails.name},</p>
                <p>Please find attached your construction cost invoice.</p>
                <p>Here are your details:</p>
                <ul>
                    <li><strong>Name:</strong> ${userDetails.name}</li>
                    <li><strong>Contact:</strong> ${userDetails.contact}</li>
                    <li><strong>Email:</strong> ${userDetails.email}</li>
                    <li><strong>Address:</strong> ${userDetails.address}</li>
                    ${userDetails.additionalInfo ? `<li><strong>Additional Info:</strong> ${userDetails.additionalInfo}</li>` : ''}
                </ul>
                <p>Thank you for choosing our services!</p>
            `;

            try {
                const response = await axios.post(
                    'https://api.brevo.com/v3/smtp/email',
                    {
                        sender: {
                            name: 'Construction Cost Calculator',
                            email: 'cegil456@gmail.com' // Replace with your verified sender email in Brevo
                        },
                        to: [
                            {
                                email: userDetails.email,
                                name: userDetails.name
                            }
                        ],
                        subject: 'Construction Cost Invoice',
                        htmlContent: emailContent,
                        attachment: [
                            {
                                content: pdfBase64,
                                name: 'construction_invoice.pdf',
                                type: 'application/pdf' // Specify the MIME type
                            }
                        ]
                    },
                    {
                        headers: {
                            'accept': 'application/json',
                            'api-key': brevoApiKey, 
                            'content-type': 'application/json'
                        }
                    }
                );
                console.log('Email sent', response.data);
                alert("Invoice sent successfully!");
            } catch (error) {
                console.error('Error sending email:', error);
                alert("Failed to send email. Please check the console for details.");
            }
        };

    return (
      
        <Box>
            <Container sx={{ mt: 5, mb: 5, maxWidth: '1100px' }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: 4,
                        justifyContent: 'center',
                        alignItems: 'stretch',
                        minHeight: '80vh'
                    }}
                >

                    {/* Left: Calculator */}
                    <Card
                        sx={{
                            flex: 1,
                            maxWidth: '500px',
                            boxShadow: 6,
                            borderRadius: '16px',
                            bgcolor: '#F5EEDC',
                            minHeight: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            transition: '0.3s',
                            '&:hover': { boxShadow: 10, transform: 'scale(1.02)' },
                            padding: '25px'
                        }}
                    >
                        <CardContent>
                            <Typography variant="h4" align="center" gutterBottom>
                                Construction Cost Calculator
                            </Typography>

                            {/* Form Fields */}
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="h6" sx={{ mb: 1 }}>Construction area in sq.ft</Typography>
                                <TextField
                                    type="number"
                                    name="area"
                                    value={formData.area}
                                    onChange={handleChange}
                                    fullWidth
                                    sx={{ fontSize: '14px' }}
                                />
                            </Box>

                            <Box sx={{ mt: 3 }}>
                                <Typography variant="h6" sx={{ mb: 1 }}>Select Construction Package</Typography>
                                <Select
                                    fullWidth
                                    name="package"
                                    value={formData.package}
                                    onChange={handleChange}
                                    sx={{
                                        fontSize: '14px',
                                        bgcolor: '#F5EEDC',
                                        color: '#183B4E'
                                    }}
                                >
                                    <MenuItem value="Basic">Basic - ₹1899/sqft</MenuItem>
                                    <MenuItem value="Standard">Standard - ₹2099/sqft</MenuItem>
                                    <MenuItem value="Premium">Premium - ₹2449/sqft</MenuItem>
                                </Select>
                            </Box>

                            <Box sx={{ mt: 3 }}>
                                <Typography variant="h6" sx={{ mb: 1 }}>Car Parking area in sq.ft (optional)</Typography>
                                <TextField
                                    type="number"
                                    name="parking"
                                    value={formData.parking}
                                    onChange={handleChange}
                                    fullWidth
                                    sx={{ fontSize: '14px' }}
                                />
                            </Box>

                            {/* Range Sliders */}
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="h6" sx={{ mb: 1 }}>How many litres of underground sump required? (optional)</Typography>
                                <Slider
                                    value={formData.sump}
                                    onChange={(e, newValue) => handleSliderChange('sump', newValue)}
                                    min={0}
                                    max={20000}
                                    step={100}
                                    valueLabelDisplay="auto"
                                    sx={{ color: '#27548A' }}
                                />
                            </Box>

                            <Box sx={{ mt: 3 }}>
                                <Typography variant="h6" sx={{ mb: 1 }}>How many person waste water recycling tank required? (optional)</Typography>
                                <Slider
                                    value={formData.recyclingTank}
                                    onChange={(e, newValue) => handleSliderChange('recyclingTank', newValue)}
                                    min={0}
                                    max={10}
                                    step={1}
                                    valueLabelDisplay="auto"
                                    sx={{ color: '#27548A' }}
                                />
                            </Box>
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="h6" sx={{ mb: 1 }}>How much feet length compound wall do you require?(optional)</Typography>
                                <TextField
                                    fullWidth
                                    type="number"
                                    name="compoundWall"
                                    value={formData.compoundWall}
                                    onChange={handleChange}
                                    placeholder="Enter length in feet"
                                />
                            </Box>

                            <Box sx={{ mt: 3 }}>
                                <Typography variant="h6" sx={{ mb: 1 }}>How much solar power do you require? (optional)</Typography>
                                <Slider
                                    value={formData.solarPower}
                                    onChange={(e, newValue) => handleSliderChange('solarPower', newValue)}
                                    min={0}
                                    max={10}
                                    step={1}
                                    valueLabelDisplay="auto"
                                    sx={{ color: '#27548A' }}
                                />
                            </Box>

                            <Button
                                variant="contained"
                                sx={{
                                    mt: 4,
                                    backgroundColor: '#27548A',
                                    color: '#F5EEDC',
                                    width: '100%',
                                    fontSize: '16px',
                                    py: 1.2
                                }}
                                onClick={handleOpenModal} // Open modal on submit
                            >
                                Submit
                            </Button>
                        </CardContent>
                    </Card>

                    {/* Right: Summary */}
                    <Card
                        sx={{
                            flex: 1,
                            maxWidth: '500px',
                            boxShadow: 6,
                            borderRadius: '16px',
                            bgcolor: '#27548A',
                            color: '#F5EEDC',
                            minHeight: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            transition: '0.3s',
                            '&:hover': { boxShadow: 10, transform: 'scale(1.02)' },
                            padding: '25px'
                        }}
                    >
                        <CardContent>
                            <Typography variant="h4" align="center" gutterBottom>
                                Total Summary
                            </Typography>

                            <Divider sx={{ my: 3 }} />

                            {[
                                { label: "What is the total construction \n area in sq.ft that you require?", price: formData.area * packagePrices[formData.package] },
                                { label: "How much car parking area \n will be added?", price: formData.parking * 1800 },
                                { label: "How many litres of underground \n sump is required?", price: formData.sump * 25 },
                                { label: "How many person waste water \n recycling tanks are needed?", price: formData.recyclingTank * 30000 },
                                { label: "How much compound wall length \n is required?", price: formData.compoundWall * 2000 },
                                { label: "How much solar power \n is required?", price: formData.solarPower * 75000 }
                            ].map((item, index) => (
                                <Box key={index} sx={{ my: 3 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-line', fontSize: '19px' }}>
                                            {item.label}
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 'bold', textAlign: 'right' }}>
                                            ₹{item.price.toLocaleString()}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}

                            <Divider sx={{ my: 3 }} />

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 2 }}>
                                <Typography variant="h6">Total Estimate</Typography>
                                <Typography variant="h6">
                                    ₹{calculateCost().toLocaleString()}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </Container>

            {/* Modal Form */}
            <Dialog open={openModal} onClose={handleCloseModal}>
                <DialogTitle>Enter Your Details</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Full Name"
                        fullWidth
                        variant="outlined"
                        sx={{ mt: 2 }}
                        onChange={(e) => setUserDetails({ ...userDetails, name: e.target.value })}
                    />
                    <TextField
                        label="Contact Number"
                        fullWidth
                        variant="outlined"
                        sx={{ mt: 2 }}
                        onChange={(e) => setUserDetails({ ...userDetails, contact: e.target.value })}
                    />
                    <TextField
                        label="Email"
                        fullWidth
                        variant="outlined"
                        sx={{ mt: 2 }}
                        onChange={(e) => setUserDetails({ ...userDetails, email: e.target.value })}
                    />
                    <TextField
                        label="Address"
                        fullWidth
                        variant="outlined"
                        sx={{ mt: 2 }}
                        onChange={(e) => setUserDetails({ ...userDetails, address: e.target.value })}
                    />
                    <TextField
                        label="Additional Information (optional)"
                        fullWidth
                        variant="outlined"
                        sx={{ mt: 2 }}
                        onChange={(e) => setUserDetails({ ...userDetails, additionalInfo: e.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal}>Cancel</Button>
                    <Button onClick={handleFormSubmit}>Submit</Button>
                </DialogActions>
            </Dialog>

            <Footer />
        </Box>
    );
};

export default CostCalculator;